import React from 'react';
import { useForm, FormProvider } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { CreateVisitDto } from '../../types/visit.types';
import { Input } from '../common/Input';
import { FileUpload } from './FileUpload';
import { Button } from '../common/Button';
import { usePatients } from '../../hooks/usePatients';
import { Select } from '../common/Select';
import { FileText, Calendar, User, Stethoscope, FileEdit } from 'lucide-react';

interface VisitFormProps {
  patientId?: number;
  onSubmit: (data: CreateVisitDto & { createPrescription?: boolean }) => void;
  isLoading?: boolean;
}

export const VisitForm: React.FC<VisitFormProps> = ({
  patientId,
  onSubmit,
  isLoading = false,
}) => {
  const { t } = useTranslation();
  const { isRTL, language } = useLanguageStore();
  const { data: patients } = usePatients();
  const [files, setFiles] = React.useState<File[]>([]);
  const [createPrescription, setCreatePrescription] = React.useState(false);

  const methods = useForm<CreateVisitDto>({
    defaultValues: {
      patientId: patientId || 0,
      visitDate: new Date().toISOString().split('T')[0],
    },
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = methods;

  React.useEffect(() => {
    if (patientId) {
      setValue('patientId', patientId);
    }
  }, [patientId, setValue]);

  const patientOptions =
    patients?.map((p) => ({
      value: p.id.toString(),
      label: `${p.name} - ${p.phone}`,
    })) || [];

  const handleFormSubmit = (data: CreateVisitDto) => {
    // Ensure patientId is a valid number greater than 0
    const patientId = typeof data.patientId === 'string' 
      ? parseInt(data.patientId, 10) 
      : data.patientId;
    
    if (!patientId || patientId <= 0) {
      return; // Validation will show error
    }
    
    onSubmit({ ...data, patientId, files, createPrescription });
  };

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(handleFormSubmit)} className="space-y-4" dir={isRTL ? 'rtl' : 'ltr'}>
      {/* 1. Patient Selection */}
      {!patientId && (
        <div className="mb-4">
          <div className="d-flex align-items-center gap-2 mb-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <User className="h-4 w-4 text-primary" />
            <span className="fw-semibold text-dark" style={{ fontSize: '0.95rem' }}>{t('prescriptions.patient')}</span>
            <span className="text-danger">*</span>
          </div>
          <Select
            required
            options={patientOptions}
            placeholder={t('prescriptions.selectPatient', 'اختر مريض')}
            error={errors.patientId?.message}
            name="patientId"
          />
        </div>
      )}

      {/* 2. Visit Date */}
      <div className="mb-4">
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem', direction: isRTL ? 'rtl' : 'ltr' }}>
          <Calendar className="h-4 w-4 text-primary" />
          <span>{t('visits.visitDate')}</span>
          <span className="text-danger">*</span>
        </label>
        <Input
          type="date"
          required
          error={errors.visitDate?.message}
          {...register('visitDate', { required: t('errors.required') })}
        />
      </div>

      {/* 3. Diagnosis */}
      <div className="mb-4">
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem', direction: isRTL ? 'rtl' : 'ltr' }}>
          <Stethoscope className="h-4 w-4 text-primary" />
          <span>{t('prescriptions.diagnosis')}</span>
        </label>
        <textarea
          className="form-control"
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            minHeight: '100px',
            fontFamily: language === 'ar' ? 'var(--font-family-arabic)' : 'var(--font-family-base)',
            fontSize: '0.95rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s',
            textAlign: isRTL ? 'right' : 'left',
          }}
          placeholder={t('prescriptions.diagnosis', 'التشخيص (اختياري)')}
          rows={3}
          {...register('diagnosis')}
        />
      </div>

      {/* 4. Notes */}
      <div className="mb-4">
        <label className="d-flex align-items-center gap-2 mb-2 fw-semibold text-dark" style={{ fontSize: '0.95rem', direction: isRTL ? 'rtl' : 'ltr' }}>
          <FileEdit className="h-4 w-4 text-primary" />
          <span>{t('prescriptions.notes')}</span>
        </label>
        <textarea
          className="form-control"
          dir={isRTL ? 'rtl' : 'ltr'}
          style={{
            minHeight: '100px',
            fontFamily: language === 'ar' ? 'var(--font-family-arabic)' : 'var(--font-family-base)',
            fontSize: '0.95rem',
            padding: '0.75rem 1rem',
            borderRadius: '0.5rem',
            border: '2px solid #e5e7eb',
            transition: 'all 0.3s',
            textAlign: isRTL ? 'right' : 'left',
          }}
          placeholder={t('prescriptions.notes', 'ملاحظات (اختياري)')}
          rows={3}
          {...register('notes')}
        />
      </div>

      {/* 5. Visit Photos */}
      <div className="mb-4">
        <FileUpload onFilesChange={setFiles} required={false} maxFiles={20} />
      </div>

      {/* 6. Create Prescription Option */}
      <div 
        className="rounded-lg p-4 transition-all"
        dir={isRTL ? 'rtl' : 'ltr'}
        style={{
          background: createPrescription 
            ? 'linear-gradient(135deg, #dbeafe 0%, #bfdbfe 100%)'
            : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
          border: `2px solid ${createPrescription ? '#3b82f6' : '#e5e7eb'}`,
        }}
      >
        <label className="d-flex align-items-start cursor-pointer" style={{ flexDirection: isRTL ? 'row-reverse' : 'row', direction: isRTL ? 'rtl' : 'ltr' }}>
          <input
            type="checkbox"
            checked={createPrescription}
            onChange={(e) => setCreatePrescription(e.target.checked)}
            className="mt-1"
            style={{
              width: '20px',
              height: '20px',
              cursor: 'pointer',
              accentColor: '#3b82f6',
              flexShrink: 0,
              marginLeft: isRTL ? '0' : '0.75rem',
              marginRight: isRTL ? '0.75rem' : '0',
            }}
          />
          <div 
            className="flex-1"
            style={{ 
              direction: isRTL ? 'rtl' : 'ltr',
            }}
          >
            <div className="d-flex align-items-center gap-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
              <FileText className={`h-4 w-4 transition-colors ${createPrescription ? 'text-primary' : 'text-muted'}`} />
              <span 
                className={`fw-semibold transition-colors ${createPrescription ? 'text-primary' : 'text-dark'}`} 
                style={{ 
                  fontSize: '0.95rem',
                }}
              >
                {t('visits.createPrescriptionAfterVisit')}
              </span>
            </div>
            <p 
              className="text-muted small mt-2 mb-0" 
              style={{ 
                fontSize: '0.875rem', 
                lineHeight: '1.5',
                direction: isRTL ? 'rtl' : 'ltr',
              }}
            >
              {t('visits.createPrescriptionHint')}
            </p>
          </div>
        </label>
      </div>

      <div style={{ marginTop: '1rem' }}>
        <Button 
          type="submit" 
          isLoading={isLoading} 
          fullWidth
          variant="primary"
          className="py-3"
        >
          {t('visits.createVisit')}
        </Button>
      </div>
    </form>
    </FormProvider>
  );
};

